import {Observable, defer, throwError, timer, of, retry} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpResponse} from "@angular/common/http";

interface RetryOptions {
  sourceFn: () => Observable<any>; // Función que regresa un Observable (la petición HTTP)
  conexionRedService: { isOnline: () => boolean }; // Servicio con método isOnline
  maxReintentos: number;
  delayMs: number;
  onRetry?: (retryCount: number) => void
}

/**
 * Reintenta una petición solo si hay conexión a internet.
 * @param sourceFn Función que devuelve un observable (la petición)
 * @param conexionRedService servicio para validar si esta en linea
 * @param maxReintentos Número máximo de reintentos
 * @param delayMs Milisegundos entre reintentos
 * @param onRetry Número de reintentos
 */
export function retryWithNetworkAware({ sourceFn, conexionRedService, maxReintentos, delayMs, onRetry}: RetryOptions): Observable<any> {
  return defer(() => {
    if (!conexionRedService.isOnline()) {
      // 🚨 si ya no hay red desde el inicio, no hago nada
      return of(getOfflineHttpResponse({ offline: true }));
    }

    return sourceFn().pipe(
      retry({
        count: maxReintentos,
        delay: (error, retryCount) => {
          if (onRetry) onRetry(retryCount);

          if (!conexionRedService.isOnline()) {
            // 🚨 corto aquí los reintentos con un error controlado
            return throwError(() => new Error('SIN_INTERNET'));
          }

          // si hay red → aplico delay normal
          return timer(delayMs);
        },
      }),
      catchError((err) => {
        if (err.message === 'SIN_INTERNET') {
          // cuando se detecta sin red → regreso la respuesta offline
          return of(getOfflineHttpResponse({ offline: true }));
        }
        return throwError(() => new Error(err));
      })
    );
  });
}

export function getOfflineHttpResponse<T>(body: T = null): HttpResponse<T> {
  return new HttpResponse<T>({
    status: 200,
    statusText: 'Offline: respuesta vacía',
    body
  });
}
